import { getPrivateSessionState } from "@/lib/api/private-session";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { useSwitchStoreMutation } from "@/shared/services/useSwitchStoreMutation";

type UseStoreSwitchResult = {
  isSwitching: boolean;
  error: string | null;
  switchTo: (nextStoreSlug: string) => Promise<void>;
};

/**
 * Shared store-switch logic — single source of truth extracted from
 * `AccountPage.handleStoreSwitch`. Consumed by the Account page (Phase 1) and,
 * later, the sidebar / login picker / mobile switchers.
 *
 * `activeStoreSlug` is the currently-active store (from the session). `switchTo`
 * no-ops when the target is empty, already the active slug, or a switch is
 * already in flight — matching AccountPage's original guard exactly.
 *
 * On success it calls `switchStore` (which clears private state before rebinding) then
 * navigates to the target store (replace). On failure it surfaces the
 * ApiJsonClientError message, falling back to the same i18n error key
 * AccountPage used previously.
 */
export function useStoreSwitch(
  locale: string,
  activeStoreSlug: string,
): UseStoreSwitchResult {
  const t = useTranslations();
  const navigate = useNavigate();
  const { mutateAsync: switchSellerStore } = useSwitchStoreMutation();

  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const switchTo = useCallback(
    async (nextStoreSlug: string) => {
      if (
        !nextStoreSlug ||
        nextStoreSlug === activeStoreSlug ||
        isSwitching
      ) {
        return;
      }
      setError(null);
      setIsSwitching(true);
      try {
        await switchSellerStore({ storeSlug: nextStoreSlug });
        navigate(getSellerPath(locale, nextStoreSlug), { replace: true });
      } catch {
        const boundStoreSlug = getPrivateSessionState().storeSlug;
        if (boundStoreSlug && boundStoreSlug !== activeStoreSlug) {
          navigate(getSellerPath(locale, boundStoreSlug), { replace: true });
          return;
        }
        // Switching stores fails for one reason the seller can act on
        // (they lost access to that store), so the copy is the same either
        // way — and never the backend's own English sentence.
        setError(t(keys.seller.storeSwitcherError));
      } finally {
        setIsSwitching(false);
      }
    },
    [activeStoreSlug, isSwitching, locale, navigate, switchSellerStore, t],
  );

  return { isSwitching, error, switchTo };
}
